import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ListShowMoreIcon } from '../../../Svgs'
import {
  createDoor,
  CreateDoorPayload,
  editWall,
  EditWallPayload,
  setSelectedToolProps,
} from '../../../../actions/construction'
import store, { RootState } from '../../../../store'
import { getEnums } from '../../../../utils/Common'
import { BuildingOpeningSize } from '../../../../models/Construction'
import { createSelector } from '@reduxjs/toolkit'
import { SceneManager } from '../../../../visualization/sceneManager'
import { ActionType } from '../../../../reducers/history'
import { EventType } from '../../../../utils/EventEmitter'
import { updateHistory } from '../../../../actions/history'

const convertDataSelector = createSelector(
  [(state: RootState) => state.construction],
  (construction) => construction.convertData,
)

const CreateDoor = () => {
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(true)
  const doorSize = useSelector(
    (state: RootState) => state.construction.selectedToolProps.doorSize,
  )

  useEffect(() => {
    const eventEmitter = SceneManager.getInstance().getEventEmitter()
    if (!eventEmitter) {
      return
    }

    const handleNewDoorCreation = (createDoorPayload: {
      door: CreateDoorPayload
      isBatchOperation: boolean
    }) => {
      dispatch(createDoor(createDoorPayload.door))

      const convertData = structuredClone(convertDataSelector(store.getState()))
      dispatch(
        updateHistory({
          convertData,
          items: [
            {
              itemId: createDoorPayload.door.id,
              actionType: ActionType.ADD,
            },
          ],
          isBatch: createDoorPayload.isBatchOperation,
        }),
      )
    }

    const handleEditWall = (editWallPayload: EditWallPayload) => {
      dispatch(editWall(editWallPayload))

      const convertData = structuredClone(convertDataSelector(store.getState()))
      dispatch(
        updateHistory({
          convertData,
          items: [
            {
              itemId: editWallPayload.id,
              actionType: ActionType.EDIT,
            },
          ],
        }),
      )
    }

    eventEmitter.on(EventType.BuildingAddDoorElement, handleNewDoorCreation)
    eventEmitter.on(EventType.BuildingEditWallElement, handleEditWall)

    return () => {
      eventEmitter.off(EventType.BuildingAddDoorElement, handleNewDoorCreation)
      eventEmitter.off(EventType.BuildingEditWallElement, handleEditWall)
    }
  }, [])

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          paddingLeft: '12px',
          paddingRight: '4px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          color='#04002E'
          sx={{
            fontFamily: 'Inter',
            fontSize: '13px',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Create a New Door Settings
        </Typography>

        <IconButton
          onClick={() => setIsOpen((prev) => !prev)}
          sx={{
            transition: 'transform 0.3s',
            transform: isOpen ? 'rotate(90deg)' : 'rotate(180deg)',
          }}
        >
          <ListShowMoreIcon
            sx={{
              fontSize: '14px',
              color: '#797979',
            }}
          />
        </IconButton>
      </Box>

      <Divider
        flexItem
        orientation='horizontal'
        sx={{
          backgroundColor: '#E4E4E4',
        }}
      />

      <Collapse in={isOpen} timeout='auto' unmountOnExit>
        <Stack
          direction='column'
          sx={{
            padding: '8px 12px',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <Typography
              sx={{
                color: '#737373',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                textAlign: 'left',
              }}
            >
              Door Size
            </Typography>

            <ToggleButtonGroup
              size='small'
              color='primary'
              value={doorSize}
              exclusive
              aria-label='Platform'
              sx={{
                flex: 1,
                borderRadius: '4px',
                backgroundColor: '#F2F2F2',
                '.MuiToggleButton-root': {
                  margin: 0,
                  border: 'none',
                  borderRadius: '4px',
                },
              }}
            >
              {getEnums(BuildingOpeningSize).map(([key, value]) => (
                <ToggleButton
                  key={key}
                  value={value}
                  onClick={() =>
                    dispatch(setSelectedToolProps({ doorSize: value }))
                  }
                  sx={{
                    width: '100%',
                    backgroundColor: '#F5F5F5',
                    '&:hover': {
                      backgroundColor: '#E5F0FF',
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#1705E5',
                      '&:hover': {
                        backgroundColor: '#1705E5',
                      },
                      '.MuiTypography-root': {
                        color: 'white',
                      },
                    },
                  }}
                >
                  <Typography
                    sx={{
                      textAlign: 'center',
                      fontFamily: 'Inter',
                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: 'normal',
                      textTransform: 'capitalize',
                      color: '#97999B',
                    }}
                  >
                    {value.toLowerCase()}
                  </Typography>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Stack>

        <Divider
          flexItem
          orientation='horizontal'
          sx={{
            backgroundColor: '#E4E4E4',
          }}
        />
      </Collapse>
    </Box>
  )
}

export default CreateDoor
